import React, { useEffect, useMemo, useState } from "react";
import api, { downloadHandover, downloadAssetReport } from "../../api";
import { toast } from "sonner";
import { Car, User, Search, FileSignature, History, PackageCheck, UserCog, FileDown, ChevronDown, CheckCircle2, CircleSlash } from "lucide-react";
import { fmtDate, contractBadge, daysToEnd } from "./adminUtils";
import { ConfirmDialog } from "./ConfirmDialog";
import { AssetHistoryDialog } from "./AssetHistoryDialog";
import { AssetReassignDialog } from "./AssetReassignDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const StatCard = ({ icon, label, value, color, testid, alert }) => (
  <div className="relative bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid={testid}>
    {alert?.count > 0 && (
      <span data-testid={`${testid}-alert`} className={`absolute -top-2 -right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm ${alert.danger ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`} title={`${alert.count} in scadenza`}>
        {alert.count} in scadenza
      </span>
    )}
    <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1a`, color }}>{icon}</span>
    <div>
      <div className="text-[22px] font-black leading-none text-[#16345c]">{value}</div>
      <div className="text-[11.5px] text-gray-500 mt-1">{label}</div>
    </div>
  </div>
);

const ExpiryCell = ({ iso }) => {
  if (!iso) return <span className="text-gray-400 text-[13px]">—</span>;
  const badge = contractBadge(iso);
  return (
    <div className="flex flex-col">
      <span className="text-[13px] text-gray-700">{fmtDate(iso)}</span>
      {badge && <span className={`w-fit mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>}
    </div>
  );
};

export const CarsTab = () => {
  const [cars, setCars] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [showReturned, setShowReturned] = useState(false);
  const [toReturn, setToReturn] = useState(null);
  const [histView, setHistView] = useState(null);
  const [toReassign, setToReassign] = useState(null);
  const [savingReassign, setSavingReassign] = useState(false);

  const load = () => api.get("/benefits").then((r) => setCars((r.data || []).filter((b) => b.car_model || b.car_plate))).catch(() => {});
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cars.filter((b) => (showReturned || !b.car_returned) && (!q || `${b.car_model || ""} ${b.car_plate || ""} ${b.car_employee_name || ""}`.toLowerCase().includes(q)));
  }, [cars, search, showReturned]);

  const handover = async (bid) => {
    try { await downloadHandover(bid, "car", "verbale_consegna_auto.pdf"); toast.success("Verbale di consegna scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const report = async (includeReturned) => {
    try { await downloadAssetReport("car", "report_parco_auto.pdf", includeReturned); toast.success("Report parco auto scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const stats = useMemo(() => {
    const active = cars.filter((b) => !b.car_returned);
    let expiring = 0, danger = false;
    active.forEach((b) => {
      const ds = [b.car_insurance_expiry, b.car_revision_expiry].map(daysToEnd).filter((d) => d !== null && d <= 30);
      if (ds.length) { expiring++; if (ds.some((d) => d < 0)) danger = true; }
    });
    return {
      active: active.length,
      assigned: active.filter((b) => b.car_employee_id).length,
      free: active.filter((b) => !b.car_employee_id).length,
      returned: cars.filter((b) => b.car_returned).length,
      expiring, danger,
    };
  }, [cars]);

  const markReturned = async () => {
    try {
      await api.put(`/benefits/${toReturn.id}`, { car_returned: true, car_returned_date: new Date().toISOString().slice(0, 10) });
      toast.success("Auto segnata come riconsegnata");
      setToReturn(null); load();
    } catch { toast.error("Errore"); }
  };

  const doReassign = async ({ id, name }) => {
    setSavingReassign(true);
    try {
      await api.put(`/benefits/${toReassign.id}`, { car_employee_id: id, car_employee_name: name, car_returned: false, car_returned_date: "" });
      toast.success(id ? `Auto riassegnata a ${name}` : "Auto lasciata senza assegnatario");
      setToReassign(null); load();
    } catch { toast.error("Errore durante la riassegnazione"); }
    finally { setSavingReassign(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 text-[13px] text-gray-600">
        <Car size={18} className="text-[#0d6cb8]" />
        Elenco delle auto aziendali definite nel Welfare, con assegnatario e scadenze. Gestisci le auto dalla pagina <b>Welfare</b>.
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="cars-stats">
        <StatCard testid="stat-cars-active" icon={<Car size={18} />} value={stats.active} label="Auto attive" color="#0d6cb8" alert={{ count: stats.expiring, danger: stats.danger }} />
        <StatCard testid="stat-cars-assigned" icon={<User size={18} />} value={stats.assigned} label="Assegnate" color="#16a34a" />
        <StatCard testid="stat-cars-free" icon={<CircleSlash size={18} />} value={stats.free} label="Disponibili (non assegnate)" color="#e67e22" />
        <StatCard testid="stat-cars-returned" icon={<PackageCheck size={18} />} value={stats.returned} label="Riconsegnate" color="#6b7280" />
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white w-full sm:w-[320px]">
          <Search size={16} className="text-gray-400" />
          <input value={search} data-testid="car-search" onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per targa, modello o dipendente..." className="flex-1 py-2 px-2 text-[14px] outline-none" />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-gray-600 cursor-pointer">
          <input type="checkbox" checked={showReturned} data-testid="car-show-returned" onChange={(e) => setShowReturned(e.target.checked)} className="accent-[#16345c]" />
          Mostra riconsegnate
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="car-report-btn" className="ml-auto flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors"><FileDown size={15} /> Report parco auto <ChevronDown size={14} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[240px]">
            <DropdownMenuItem data-testid="car-report-all" onClick={() => report(true)} className="text-[13px] cursor-pointer gap-2"><CheckCircle2 size={15} className="text-[#16345c]" /> Tutti i beni (con riconsegnate)</DropdownMenuItem>
            <DropdownMenuItem data-testid="car-report-active" onClick={() => report(false)} className="text-[13px] cursor-pointer gap-2"><Car size={15} className="text-[#0d6cb8]" /> Solo auto attive</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="cars-registry">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Auto</th>
              <th className="text-left px-4 py-3 font-bold">Targa</th>
              <th className="text-left px-4 py-3 font-bold">Assegnata a</th>
              <th className="text-left px-4 py-3 font-bold">Scad. assicurazione</th>
              <th className="text-left px-4 py-3 font-bold">Scad. revisione</th>
              <th className="text-right px-4 py-3 font-bold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors ${b.car_returned ? "opacity-60" : ""}`} data-testid={`car-row-${b.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-blue-50 text-[#0d6cb8] flex items-center justify-center shrink-0"><Car size={16} /></span>
                    <span className="text-[14px] font-bold text-[#16345c]">{b.car_model || b.title}</span>
                    {b.car_returned && <span className="text-[10px] font-bold uppercase bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">Riconsegnata {fmtDate(b.car_returned_date)}</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {b.car_plate ? <span className="font-mono uppercase tracking-wide text-[13px] bg-gray-100 px-2 py-0.5 rounded-sm">{b.car_plate}</span> : <span className="text-gray-400 text-[13px]">—</span>}
                </td>
                <td className="px-4 py-3 text-[13px] text-gray-700">
                  {b.car_employee_name ? <span className="flex items-center gap-1.5"><User size={13} className="text-gray-400" /> {b.car_employee_name}</span> : <span className="text-gray-400">Non assegnata</span>}
                </td>
                <td className="px-4 py-3"><ExpiryCell iso={b.car_insurance_expiry} /></td>
                <td className="px-4 py-3"><ExpiryCell iso={b.car_revision_expiry} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setHistView({ title: `Auto · ${b.car_model || b.car_plate || ""}`, history: b.car_history })} data-testid={`car-history-${b.id}`} title="Storico assegnazioni" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-gray-500 hover:bg-gray-100 transition-colors"><History size={15} /></button>
                    <button onClick={() => setToReassign({ id: b.id, label: `Auto · ${b.car_model || b.car_plate || ""}`, currentEmployeeId: b.car_employee_id || "none" })} data-testid={`car-reassign-${b.id}`} title="Riassegna rapida" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-[#0d6cb8] hover:bg-blue-50 transition-colors"><UserCog size={15} /></button>
                    <button onClick={() => handover(b.id)} data-testid={`car-handover-${b.id}`} title="Verbale di consegna (PDF)" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-[#16345c] hover:bg-blue-50 transition-colors"><FileSignature size={15} /></button>
                    {!b.car_returned && <button onClick={() => setToReturn(b)} data-testid={`car-return-${b.id}`} title="Segna come riconsegnata" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-50 transition-colors"><PackageCheck size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessuna auto {showReturned ? "" : "attiva "}trovata.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog open={!!toReturn} onCancel={() => setToReturn(null)} onConfirm={markReturned} confirmLabel="Segna riconsegnata" confirmClass="bg-emerald-600 hover:bg-emerald-700" title="Segnare l'auto come riconsegnata?">
        L'auto <b>{toReturn?.car_model}</b> ({toReturn?.car_plate}) verrà segnata come riconsegnata in data odierna e uscirà dal registro attivo. Puoi rivederla con "Mostra riconsegnate".
      </ConfirmDialog>

      <AssetHistoryDialog open={!!histView} onClose={() => setHistView(null)} title={histView?.title} history={histView?.history} />

      <AssetReassignDialog open={!!toReassign} onClose={() => setToReassign(null)} onConfirm={doReassign} employees={employees} target={toReassign} saving={savingReassign} />
    </div>
  );
};
